import { activateDiagram } from '../lib/diagrams';

const IMG = {
  analytics:
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1400&q=80',
  security:
    'https://images.unsplash.com/photo-1562813733-b31f71025d54?auto=format&fit=crop&w=1400&q=80',
  blueprint:
    'https://images.unsplash.com/photo-1507206130118-b5907f817163?auto=format&fit=crop&w=1400&q=80',
  career:
    'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80',
  dashboard:
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1400&q=80',
  teamMeeting:
    'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=1400&q=80',
};

// ——————————————————————————————————————————————————————————————
//  MODULE 6 — SAP Data & Reporting Concepts (4h)
// ——————————————————————————————————————————————————————————————
const module6 = {
  id: 'm6',
  number: 6,
  title: 'SAP Data & Reporting Concepts',
  duration: '4 Hours',
  minutes: 240,
  icon: 'BarChart3',
  tagline: 'From raw transactions to trustworthy dashboards.',
  takeaway:
    'You will understand the master-data lifecycle, why data consistency is non-negotiable, how SAP reporting is organized, and what SAP Analytics Cloud brings to the table.',
  subTopics: [
    {
      id: 'm6-s1',
      title: 'Master Data Lifecycle',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'Master data is never set-and-forget. It flows through a clear lifecycle that the business must govern.',
        },
        {
          type: 'ol',
          items: [
            'Request / Create — a new customer, vendor, material is requested.',
            'Enrich — data stewards complete views (sales org, plant, finance).',
            'Approve — workflow routes through finance, procurement, tax.',
            'Distribute — syndicated to downstream systems (CRM, BW, portals).',
            'Maintain — periodic updates (address change, new bank details).',
            'Block &amp; archive — inactive records are blocked, then archived.',
          ],
        },
        {
          type: 'callout',
          variant: 'info',
          title: 'SAP MDG',
          text:
            'Large customers often run SAP Master Data Governance (MDG) — a dedicated product to orchestrate this lifecycle with workflow, validation and audit.',
        },
      ],
    },
    {
      id: 'm6-s2',
      title: 'Data Consistency & Integrity',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'Consistency means the same fact is identical across every screen. Integrity means the fact is correct and constrained. SAP enforces both through technical and process controls.',
        },
        {
          type: 'table',
          headers: ['Control type', 'Example'],
          rows: [
            ['Referential integrity', 'You cannot post a PO to a vendor that doesn’t exist.'],
            ['Domain validation', 'Posting date must be in an open period.'],
            ['Authorization', 'Only AP clerks can post incoming invoices.'],
            ['Workflow approval', 'High-value PO requires CFO sign-off.'],
            ['Audit log', 'Every change to master data is tracked in change documents.'],
          ],
        },
        {
          type: 'callout',
          variant: 'warn',
          title: 'Garbage in, garbage out',
          text:
            'A bad item master (wrong UOM, wrong tax code) quietly ruins thousands of downstream transactions. Master-data quality is strategic.',
        },
      ],
    },
    {
      id: 'm6-s3',
      title: 'SAP Reporting Basics',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text: 'SAP offers several layers of reporting, ranging from simple lists to enterprise dashboards.',
        },
        {
          type: 'table',
          headers: ['Layer', 'Example tools', 'Use case'],
          rows: [
            ['Transactional lists', 'FBL3N, MB51, VA05', 'Operational day-to-day queries.'],
            ['Standard reports', 'S_ALR_* T-Codes, financial statements', 'Statutory and managerial.'],
            ['Ad-hoc / Query tools', 'SAP Query (SQ01), ABAP lists', 'Power users build their own.'],
            ['BW / Data Warehouse', 'SAP BW/4HANA', 'Trend analysis, multi-system.'],
            ['Modern analytics', 'SAP Analytics Cloud (SAC), Fiori KPI tiles', 'Dashboards, planning, ML.'],
          ],
        },
        { type: 'img', src: IMG.analytics, caption: 'Figure 6.1 — Modern SAP analytics brings real-time dashboards.' },
      ],
    },
    {
      id: 'm6-s4',
      title: 'SAP Analytics Cloud & Embedded Analytics',
      readingTime: '25 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP Analytics Cloud (SAC) is SAP’s cloud BI tool — stories, dashboards, planning and predictive, all in one browser UI.',
        },
        {
          type: 'ul',
          items: [
            'Direct live connection to S/4HANA — no ETL needed.',
            'Drag-and-drop story building.',
            'Built-in Smart Insights and Predictive Scenarios.',
            'Planning — budget, forecast, what-if in the same tool.',
          ],
        },
        { type: 'img', src: IMG.dashboard, caption: 'Figure 6.2 — Typical SAC story visualizing operational KPIs.' },
        { type: 'h', text: 'Embedded analytics in S/4HANA' },
        {
          type: 'p',
          text:
            'S/4HANA also ships thousands of CDS-view-based analytical apps out of the box — KPI tiles, Overview Pages and Analytical List Pages — so users don’t need to leave Fiori to get insight.',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'SAP MDG stands for…',
      options: [
        'Master Data Governance',
        'Material Document Generator',
        'Mobile Device Gateway',
        'Monthly Depreciation Group',
      ],
      correct: 0,
      explanation: 'SAP MDG = Master Data Governance.',
    },
    {
      q: 'Which is a cloud BI tool from SAP?',
      options: ['SAP BW', 'SAP Analytics Cloud', 'SAP GUI', 'SAP SE16'],
      correct: 1,
      explanation: 'SAP Analytics Cloud (SAC) is SAP’s cloud BI + planning + predictive tool.',
    },
    {
      q: 'Why is master-data quality so important?',
      options: [
        'It makes the GUI faster',
        'Bad master data silently corrupts all downstream transactions and reports',
        'It reduces license cost',
        'It turns on Fiori',
      ],
      correct: 1,
      explanation: 'Every transaction references master data — if master is wrong, the whole chain is wrong.',
    },
    {
      q: 'A referential-integrity check prevents…',
      options: [
        'Posting a PO to a non-existent vendor',
        'Running the MRP overnight',
        'Printing invoices',
        'Logging in with the wrong password',
      ],
      correct: 0,
      explanation: 'Referential integrity means every foreign key must exist in the parent table (e.g., vendor).',
    },
  ],
};

// ——————————————————————————————————————————————————————————————
//  MODULE 7 — SAP Security & Authorizations (2h)
// ——————————————————————————————————————————————————————————————
const module7 = {
  id: 'm7',
  number: 7,
  title: 'SAP Security & Authorizations',
  duration: '2 Hours',
  minutes: 120,
  icon: 'ShieldCheck',
  tagline: 'Who can do what — the SAP authorization concept in a nutshell.',
  takeaway:
    'You will be able to explain the SAP security model: users, roles, profiles and authorization objects; and you will recognize classic risks such as Segregation of Duties (SoD).',
  subTopics: [
    {
      id: 'm7-s1',
      title: 'Users & Roles',
      readingTime: '15 min',
      blocks: [
        {
          type: 'p',
          text:
            'In SAP, every login is a named user. Users get access not by direct permission lists but through Roles. Roles bundle authorizations and are assigned to users.',
        },
        {
          type: 'table',
          headers: ['Concept', 'Description', 'T-Code'],
          rows: [
            ['User', 'An individual login.', 'SU01'],
            ['Role (single)', 'A bundle of authorizations.', 'PFCG'],
            ['Role (composite)', 'A collection of single roles.', 'PFCG'],
            ['Profile', 'Technical object generated from a role.', '— (auto)'],
          ],
        },
        { type: 'img', src: IMG.security, caption: 'Figure 7.1 — Every click in SAP is permission-checked.' },
      ],
    },
    {
      id: 'm7-s2',
      title: 'Authorization Concept',
      readingTime: '15 min',
      blocks: [
        {
          type: 'p',
          text:
            'Authorizations in SAP are built from Authorization Objects, each with one or more fields and allowed values. A role grants values for each object (e.g., Company Code = 1000, Activity = Display).',
        },
        {
          type: 'ul',
          items: [
            'Authorization object — e.g., F_BKPF_BUK (FI document for company code).',
            'Field — e.g., BUKRS (Company Code), ACTVT (Activity).',
            'Activity values — 01 = Create, 02 = Change, 03 = Display, 06 = Delete.',
            'Authorization check — done at runtime by ABAP code (AUTHORITY-CHECK).',
          ],
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Debugging access issues',
          text:
            'When a user gets "No authorization", open SU53 — it shows exactly which object + value was missing.',
        },
      ],
    },
    {
      id: 'm7-s3',
      title: 'Data Security & Segregation of Duties',
      readingTime: '15 min',
      blocks: [
        { type: 'h', text: 'Segregation of Duties (SoD)' },
        {
          type: 'p',
          text:
            'No single user should be able to complete a risky end-to-end process alone. Classic SoD rules:',
        },
        {
          type: 'ul',
          items: [
            'The person who creates a vendor should not be able to pay the vendor.',
            'The person who creates a PO should not be able to approve it.',
            'The person who posts a journal should not be able to approve period-close.',
          ],
        },
        { type: 'h', text: 'SAP GRC' },
        {
          type: 'p',
          text:
            'SAP GRC (Governance, Risk &amp; Compliance) continuously scans role assignments for SoD violations and provides workflows for mitigation and emergency access (firefighter).',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'In SAP, users get access through…',
      options: ['Direct permissions', 'Roles (which bundle authorizations)', 'Shell scripts', 'Database grants'],
      correct: 1,
      explanation: 'Authorizations are bundled into Roles, and Roles are assigned to Users.',
    },
    {
      q: 'Which T-Code maintains SAP users?',
      options: ['PFCG', 'SU01', 'SE16', 'VA01'],
      correct: 1,
      explanation: 'SU01 = maintain users; PFCG = maintain roles.',
    },
    {
      q: 'What does Segregation of Duties (SoD) prevent?',
      options: [
        'Users from logging in twice',
        'One person from completing an end-to-end risky process alone',
        'Developers from writing ABAP',
        'SAP from running on Linux',
      ],
      correct: 1,
      explanation: 'SoD splits risky activities across multiple people to prevent fraud &amp; error.',
    },
    {
      q: 'Which tool helps a user find the missing authorization after getting "No authorization"?',
      options: ['SU53', 'SM37', 'SE38', 'FB01'],
      correct: 0,
      explanation: 'SU53 shows the most recent failed authorization check for the user.',
    },
  ],
};

// ——————————————————————————————————————————————————————————————
//  MODULE 8 — SAP Implementation Lifecycle (4h)
// ——————————————————————————————————————————————————————————————
const module8 = {
  id: 'm8',
  number: 8,
  title: 'SAP Implementation Lifecycle',
  duration: '4 Hours',
  minutes: 240,
  icon: 'Rocket',
  tagline: 'From project kickoff to hypercare — the SAP Activate way.',
  takeaway:
    'You will understand SAP Activate, the six project phases, the roles on an SAP project, and how a typical go-live is orchestrated.',
  subTopics: [
    {
      id: 'm8-s1',
      title: 'SAP Activate Methodology',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP Activate is SAP’s unified, agile methodology for delivering SAP projects. It replaces the older ASAP methodology and is used for all modern S/4HANA and cloud projects.',
        },
        { type: 'img', src: activateDiagram, caption: 'Figure 8.1 — Six Activate phases with key roles.' },
        {
          type: 'ul',
          items: [
            'Built on three pillars: SAP best practices, guided configuration, agile delivery.',
            'Applies to public cloud, private cloud and on-premise.',
            'Agile sprints inside Explore &amp; Realize.',
            'Pre-delivered accelerators — content, test scripts, sample data.',
          ],
        },
      ],
    },
    {
      id: 'm8-s2',
      title: 'The Six Phases in Detail',
      readingTime: '25 min',
      blocks: [
        {
          type: 'table',
          headers: ['Phase', 'Goal', 'Key deliverables'],
          rows: [
            ['Discover', 'Build the business case.', 'Value assessment, trial system, solution roadmap.'],
            ['Prepare', 'Mobilize the project.', 'Charter, plan, team onboarding, sandbox system.'],
            ['Explore', 'Fit-to-standard workshops.', 'Gap list, backlog, solution design.'],
            ['Realize', 'Configure, build, test.', 'Configured system, unit + integration test results.'],
            ['Deploy', 'Cutover and go-live.', 'Data migration, UAT sign-off, hypercare plan.'],
            ['Run', 'Operate &amp; continuously improve.', 'Support model, release calendar, optimization roadmap.'],
          ],
        },
        { type: 'img', src: IMG.blueprint, caption: 'Figure 8.2 — Activate is iterative, not waterfall.' },
      ],
    },
    {
      id: 'm8-s3',
      title: 'Roles on an SAP Project',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'SAP projects are team sports. Knowing who does what helps you collaborate and, later, understand career paths.',
        },
        {
          type: 'table',
          headers: ['Role', 'Responsibilities'],
          rows: [
            ['Project Sponsor', 'Business leader owning the outcome and budget.'],
            ['Project Manager', 'Plan, schedule, budget, risks, status.'],
            ['Solution Architect', 'End-to-end solution &amp; integration design.'],
            ['Functional Consultant (per module)', 'Process design, configuration, testing, training.'],
            ['Technical / ABAP Consultant', 'Custom development, enhancements, interfaces.'],
            ['Integration / CPI Consultant', 'Connects SAP with 3rd-party systems.'],
            ['Basis Administrator', 'Landscape, performance, transports, security.'],
            ['Data Migration Lead', 'ETL legacy ⇒ SAP master &amp; open transactions.'],
            ['Change Management Lead', 'User training, comms, adoption.'],
            ['Key Users / Process Owners', 'Business SMEs validating the design.'],
          ],
        },
        { type: 'img', src: IMG.teamMeeting, caption: 'Figure 8.3 — Functional, technical and business teams collaborating.' },
      ],
    },
    {
      id: 'm8-s4',
      title: 'Cutover, Go-Live and Hypercare',
      readingTime: '20 min',
      blocks: [
        {
          type: 'p',
          text:
            'Go-live is not a switch — it is a carefully planned cutover weekend followed by weeks of hyper-attentive support.',
        },
        { type: 'h', text: 'Cutover plan' },
        {
          type: 'ul',
          items: [
            'Final data migration (master + open balances) into PROD.',
            'Reconciliation of balances vs legacy.',
            'User role assignments final.',
            'Business freeze on legacy, go-live announcement.',
            'Smoke tests in PROD.',
          ],
        },
        { type: 'h', text: 'Hypercare (first 2–6 weeks)' },
        {
          type: 'p',
          text:
            'Project team stays on-site / on-call. Tickets are triaged daily; patches are prioritized; users are coached in real time. Successful hypercare is the difference between a smooth go-live and chaos.',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'The six phases of SAP Activate are…',
      options: [
        'Discover, Prepare, Explore, Realize, Deploy, Run',
        'Plan, Design, Build, Test, Deploy, Support',
        'Alpha, Beta, RC, GA, Patch, EOL',
        'Idea, Prototype, MVP, Launch, Scale, Exit',
      ],
      correct: 0,
      explanation: 'Activate = Discover → Prepare → Explore → Realize → Deploy → Run.',
    },
    {
      q: '"Fit-to-standard" workshops happen primarily in which phase?',
      options: ['Discover', 'Prepare', 'Explore', 'Run'],
      correct: 2,
      explanation: 'Explore is where you compare SAP best practices to your business needs and identify gaps.',
    },
    {
      q: 'Who is responsible for the configuration inside a module like SD or MM?',
      options: ['Project Sponsor', 'Basis Admin', 'Functional Consultant', 'Change Manager'],
      correct: 2,
      explanation: 'Functional Consultants configure their module and support testing &amp; training.',
    },
    {
      q: 'Hypercare is…',
      options: [
        'A marketing campaign before go-live',
        'An intense support period immediately after go-live',
        'Healthcare insurance for consultants',
        'A hardware monitoring dashboard',
      ],
      correct: 1,
      explanation: 'Hypercare = heightened post-go-live support to stabilize the system and users.',
    },
  ],
};

// ——————————————————————————————————————————————————————————————
//  MODULE 9 — Career Pathways in SAP (2h)
// ——————————————————————————————————————————————————————————————
const module9 = {
  id: 'm9',
  number: 9,
  title: 'Career Pathways in SAP',
  duration: '2 Hours',
  minutes: 120,
  icon: 'GraduationCap',
  tagline: 'From entry-level to SAP architect — plan your career.',
  takeaway:
    'You will know the major SAP career tracks (functional vs technical), the most valuable certifications, and a practical plan to land your first SAP role.',
  subTopics: [
    {
      id: 'm9-s1',
      title: 'Functional vs Technical vs Hybrid Roles',
      readingTime: '20 min',
      blocks: [
        {
          type: 'table',
          headers: ['Track', 'Examples', 'Who it suits'],
          rows: [
            ['Functional', 'SAP MM, SD, FICO, HCM, PP consultant', 'Business graduates, domain experts (finance, supply chain).'],
            ['Technical', 'ABAP developer, Basis admin, Fiori/UI5 dev, integration (CPI)', 'Software engineers, CS graduates.'],
            ['Hybrid / Architect', 'Solution architect, S/4HANA conversion lead', 'Senior consultants with 8+ years.'],
            ['Analytics', 'SAC, BW/4HANA, Datasphere', 'Data professionals.'],
            ['GRC / Security', 'SAP GRC, SoD analysis, identity management', 'Audit, risk &amp; compliance backgrounds.'],
          ],
        },
        { type: 'img', src: IMG.career, caption: 'Figure 9.1 — SAP careers span many specializations.' },
      ],
    },
    {
      id: 'm9-s2',
      title: 'Popular SAP Certifications',
      readingTime: '15 min',
      blocks: [
        {
          type: 'p',
          text:
            'Certifications are not mandatory but they demonstrably accelerate hiring — especially for freshers. SAP offers certifications at Associate, Specialist and Professional levels.',
        },
        {
          type: 'table',
          headers: ['Certification (sample)', 'For'],
          rows: [
            ['C_S4CFI — S/4HANA Cloud Finance', 'FICO cloud functional consultants.'],
            ['C_TS4FI — S/4HANA Financial Accounting', 'On-prem FI consultants.'],
            ['C_TS452 — S/4HANA Sourcing &amp; Procurement', 'MM consultants.'],
            ['C_TS462 — S/4HANA Sales', 'SD consultants.'],
            ['C_HCMP — SAP HCM', 'Payroll &amp; HR.'],
            ['C_ACTIVATE — SAP Activate Project Manager', 'Project managers / functional leads.'],
            ['C_ABAPD — ABAP Cloud Developer', 'Developers on Clean Core.'],
          ],
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Plan wisely',
          text:
            'Pick one module (e.g., SAP MM or SAP FI) and go deep. "Jack-of-all-modules" is not a credible profile at entry-level.',
        },
      ],
    },
    {
      id: 'm9-s3',
      title: 'How to Start a Career in SAP',
      readingTime: '20 min',
      blocks: [
        {
          type: 'ol',
          items: [
            'Pick a module aligned with your background (Finance grads → FICO; Engg → PP/MM/PM; IT → ABAP/Basis).',
            'Learn fundamentals — this course is step 1. Then the openSAP / SAP Learning Hub courses.',
            'Get hands-on — use an SAP S/4HANA trial, SAP Business Technology Platform free tier, or training partner labs.',
            'Target a certification within 3–6 months.',
            'Build a portfolio — document 2-3 end-to-end business scenarios on LinkedIn / GitHub.',
            'Apply for entry-level roles — associate consultant, graduate program, SI partner freshers’ batch.',
            'Network — SAP Community, LinkedIn groups, SAP TechEd &amp; local SAP user groups.',
          ],
        },
        {
          type: 'callout',
          variant: 'example',
          title: 'A real success path',
          text:
            'Many consultants we know started as business-process analysts at a system-integrator (Accenture, Infosys, TCS, Capgemini, IBM, Deloitte), rotated through 2 projects in 18 months, certified, and then moved up to senior functional consultant in 3 years.',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'Which of the following is a FUNCTIONAL SAP role?',
      options: ['ABAP developer', 'Basis administrator', 'SAP SD consultant', 'Network engineer'],
      correct: 2,
      explanation: 'SD consultant is a functional role; ABAP and Basis are technical.',
    },
    {
      q: 'Which certification is most relevant to a fresher wanting to become an SAP finance consultant?',
      options: ['C_TS4FI', 'C_ABAPD', 'C_S4HDEV', 'C_ACTIVATE'],
      correct: 0,
      explanation: 'C_TS4FI validates S/4HANA Financial Accounting — classic fresher FICO cert.',
    },
    {
      q: 'A realistic first SAP role in an SI partner is typically…',
      options: ['CIO', 'Solution Architect', 'Associate / Junior Consultant', 'CTO'],
      correct: 2,
      explanation: 'Entry-level roles are Associate / Junior / Graduate Consultant.',
    },
  ],
};

// ——————————————————————————————————————————————————————————————
//  MODULE 10 — Capstone Project (4h)
// ——————————————————————————————————————————————————————————————
const module10 = {
  id: 'm10',
  number: 10,
  title: 'Capstone Project — Simulated SAP Business Execution',
  duration: '4 Hours',
  minutes: 240,
  icon: 'Award',
  tagline: 'Put everything together: analyze a company, map its processes, design its SAP footprint.',
  takeaway:
    'You will complete a full simulated SAP mapping exercise for a fictional company — identifying modules, drawing P2P &amp; O2C flows, and documenting a mini business blueprint.',
  subTopics: [
    {
      id: 'm10-s1',
      title: 'Your Mission — Meet Aurora Motors',
      readingTime: '15 min',
      blocks: [
        {
          type: 'p',
          text:
            'Aurora Motors is a mid-sized EV manufacturer with plants in Pune and Chennai, sales offices in 12 countries, and 8,500 employees. The CFO wants to move the entire business to SAP S/4HANA Cloud over the next 24 months.',
        },
        {
          type: 'p',
          text:
            'Your job is to produce a one-page solution sketch: which SAP modules does Aurora need, how do the core processes flow, and where are the integration points.',
        },
      ],
    },
    {
      id: 'm10-s2',
      title: 'Step 1 — Map the Business Processes',
      readingTime: '20 min',
      blocks: [
        {
          type: 'ol',
          items: [
            'List Aurora’s top 5 business processes (e.g., manufacture an EV, sell to a dealer, procure battery cells, run monthly payroll, close the books).',
            'For each, identify the mega-process bucket (P2P / O2C / H2R / R2R).',
            'Draw a simple flow — boxes with arrows — of the 4–6 key steps.',
          ],
        },
        {
          type: 'callout',
          variant: 'tip',
          title: 'Use this template',
          text:
            'Process → Trigger → Steps (in order) → Actors → SAP module(s) → Output. Repeat for 3–5 processes.',
        },
      ],
    },
    {
      id: 'm10-s3',
      title: 'Step 2 — Identify SAP Modules & Integration',
      readingTime: '20 min',
      blocks: [
        {
          type: 'table',
          headers: ['Area', 'SAP Module / Solution'],
          rows: [
            ['Procurement &amp; inventory', 'SAP MM + (optionally SAP Ariba)'],
            ['Sales &amp; distribution', 'SAP SD'],
            ['Manufacturing', 'SAP PP (+ QM + PM)'],
            ['Finance &amp; Controlling', 'SAP FICO + Group Reporting'],
            ['HR &amp; Payroll', 'SAP SuccessFactors Employee Central + Payroll'],
            ['Analytics', 'SAP Analytics Cloud'],
            ['Expenses &amp; travel', 'SAP Concur'],
            ['Integration backbone', 'SAP Integration Suite (CPI)'],
          ],
        },
        {
          type: 'p',
          text:
            'Identify 2-3 integration points — e.g., dealer portal ↔ SAP SD via APIs, bank statement ingestion into FI, SuccessFactors → FI for payroll posting.',
        },
      ],
    },
  ],
  quiz: [
    {
      q: 'Aurora Motors manufactures EVs. Which SAP module schedules the assembly line?',
      options: ['SAP SD', 'SAP MM', 'SAP PP', 'SAP HCM'],
      correct: 2,
      explanation: 'Production Planning (PP) runs manufacturing.',
    },
    {
      q: 'A dealer portal talking to SAP SD via APIs is an example of…',
      options: ['Master data governance', 'Integration (CPI / Integration Suite)', 'A T-Code', 'A payroll posting'],
      correct: 1,
      explanation: 'External system ↔ SAP over APIs is an integration, typically via SAP Integration Suite.',
    },
  ],
};

export const MODULES_PART3 = [module6, module7, module8, module9, module10];
